/**
 * 알림톡 발송 이력 조회 페이지.
 */
import { useEffect, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import {
  kakaoAlimLogApi,
  KakaoAlimLogItem,
  KakaoAlimLogPage,
  STATUS_LABEL,
  STATUS_COLOR,
} from '../../services/api/kakaoAlimLog';

const STATUS_OPTIONS = ['', 'SUCCESS', 'SKIPPED', 'FAILED', 'STUBBED'];
const TRIGGER_OPTIONS = [
  '',
  'USER_RESERVE_APPROVED',
  'USER_RESERVE_CANCELED',
  'USER_NOTICE_SENT',
  'BIZ_RESERVE_REQUEST',
  'USER_PAYMENT_NOTICE',
];

/**
 * SKIPPED/FAILED 사유를 운영자가 즉시 파악 가능한 카테고리 태그로 요약.
 * resultMessage 원문을 키워드 매칭하여 우선순위별 매핑.
 */
type SkipReason = {
  label: string;
  color: string;
  icon: string;
};

function parseSkipReason(message: string): SkipReason {
  const m = message.toLowerCase();
  if (m.includes('크레딧') || m.includes('잔액') || m.includes('use_kakao_alim'))
    return { label: '크레딧 부족', color: 'bg-orange-100 text-orange-700', icon: '💰' };
  if (m.includes('미승인') || m.includes('rej') || m.includes('req') || m.includes('reg'))
    return { label: '검수 미승인', color: 'bg-yellow-100 text-yellow-700', icon: '📋' };
  if (m.includes('미발견') || m.includes('템플릿'))
    return { label: '템플릿 미등록', color: 'bg-red-100 text-red-700', icon: '❓' };
  if (m.includes('전화번호') || m.includes('수신자'))
    return { label: '수신자 오류', color: 'bg-gray-200 text-gray-700', icon: '📵' };
  if (m.includes('templatecode') || m.includes('trigger'))
    return { label: '트리거 오류', color: 'bg-red-100 text-red-700', icon: '⚠' };
  if (m.includes('알리고') || m.includes('아리고') || m.includes('api'))
    return { label: '알리고 API 실패', color: 'bg-red-100 text-red-700', icon: '🔌' };
  return { label: '기타', color: 'bg-gray-100 text-gray-600', icon: 'ⓘ' };
}

function SkipReasonTag({ message }: { message: string }) {
  if (!message) return null;
  const r = parseSkipReason(message);
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium ${r.color}`}
      title={message}
    >
      <span>{r.icon}</span>
      <span>{r.label}</span>
    </span>
  );
}

export function KakaoAlimLogsPage() {
  const [page, setPage] = useState<KakaoAlimLogPage>({
    page: 0, size: 30, total: 0, totalPages: 0, items: [],
  });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<KakaoAlimLogItem | null>(null);

  const [q, setQ] = useState({
    triggerCode: '',
    status: '',
    fromDate: '',
    toDate: '',
    keyword: '',
  });
  const [pg, setPg] = useState(0);

  const load = async (nextPage = pg) => {
    setLoading(true);
    try {
      const res = await kakaoAlimLogApi.list({
        triggerCode: q.triggerCode || undefined,
        status: q.status || undefined,
        fromDate: q.fromDate || undefined,
        toDate: q.toDate || undefined,
        keyword: q.keyword || undefined,
        page: nextPage,
        size: 30,
      });
      setPage(res);
      setPg(nextPage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = () => load(0);

  const fmt = (dt?: string | null) => (dt ? dt.replace('T', ' ').slice(0, 19) : '-');

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">알림톡 발송 이력</h1>
        <p className="mt-1 text-sm text-gray-500">
          KakaoAlimService.send() 호출 결과 이력 — SUCCESS/SKIPPED/FAILED/STUBBED 별 필터·검색.
        </p>
      </header>

      {/* 필터 */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1">트리거</label>
          <select
            value={q.triggerCode}
            onChange={(e) => setQ({ ...q, triggerCode: e.target.value })}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          >
            {TRIGGER_OPTIONS.map((t) => <option key={t} value={t}>{t || '전체'}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">상태</label>
          <select
            value={q.status}
            onChange={(e) => setQ({ ...q, status: e.target.value })}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          >
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s ? STATUS_LABEL[s] : '전체'}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">시작일</label>
          <input
            type="date"
            value={q.fromDate}
            onChange={(e) => setQ({ ...q, fromDate: e.target.value })}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">종료일</label>
          <input
            type="date"
            value={q.toDate}
            onChange={(e) => setQ({ ...q, toDate: e.target.value })}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-gray-600 mb-1">수신자 검색 (phone/name)</label>
          <input
            type="text"
            value={q.keyword}
            onChange={(e) => setQ({ ...q, keyword: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            placeholder="01012345678 또는 홍길동"
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
          />
        </div>
        <button
          onClick={onSearch}
          className="flex items-center gap-1 rounded bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <Search size={14} /> 조회
        </button>
        <button
          onClick={() => load(pg)}
          className="flex items-center gap-1 rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
        >
          <RefreshCw size={14} /> 새로고침
        </button>
      </div>

      {/* 리스트 */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="border-b border-gray-200 px-3 py-2 text-xs font-medium text-gray-500 flex justify-between">
          <span>전체 {page.total.toLocaleString()}건</span>
          <span>페이지 {page.page + 1} / {Math.max(1, page.totalPages)}</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs">
            <tr>
              <th className="px-3 py-2 text-left w-[140px]">발송 시각</th>
              <th className="px-3 py-2 text-left">트리거</th>
              <th className="px-3 py-2 text-left w-[130px]">수신자</th>
              <th className="px-3 py-2 text-left w-[90px]">회사</th>
              <th className="px-3 py-2 text-left w-[80px]">상태</th>
              <th className="px-3 py-2 text-left w-[60px]">차감</th>
              <th className="px-3 py-2 text-left">메시지 / 사유</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-10 text-center text-gray-400">불러오는 중...</td></tr>
            ) : page.items.length === 0 ? (
              <tr><td colSpan={7} className="py-10 text-center text-gray-400">이력이 없습니다.</td></tr>
            ) : (
              page.items.map((h) => (
                <tr
                  key={h.sendSeq}
                  onClick={() => setSelected(h)}
                  className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-3 py-2 text-xs text-gray-500">{fmt(h.dtRegist)}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">{h.triggerCode}</div>
                    {h.templateName && <div className="text-xs text-gray-400">{h.templateName}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <div>{h.receiverPhone}</div>
                    {h.receiverName && <div className="text-xs text-gray-400">{h.receiverName}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">{h.companySeq ?? '-'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs ${STATUS_COLOR[h.resultStatus] ?? ''}`}>
                      {STATUS_LABEL[h.resultStatus] ?? h.resultStatus}
                    </span>
                    {/* 스킵/실패는 원인 태그로 요약 표시 */}
                    {(h.resultStatus === 'SKIPPED' || h.resultStatus === 'FAILED') && (
                      <div className="mt-1">
                        <SkipReasonTag message={h.resultMessage ?? ''} />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{h.creditAmount ? `${h.creditAmount}크` : '-'}</td>
                  <td className="px-3 py-2 text-xs text-gray-600 truncate max-w-[300px]"
                      title={h.resultMessage ?? undefined}>
                    {h.resultMessage ?? ''}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {page.totalPages > 1 && (
        <div className="flex justify-center gap-1">
          <button
            onClick={() => load(0)}
            disabled={pg === 0}
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40"
          >««</button>
          <button
            onClick={() => load(Math.max(0, pg - 1))}
            disabled={pg === 0}
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40"
          >«</button>
          <span className="px-3 py-1 text-sm">{pg + 1} / {page.totalPages}</span>
          <button
            onClick={() => load(Math.min(page.totalPages - 1, pg + 1))}
            disabled={pg >= page.totalPages - 1}
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40"
          >»</button>
          <button
            onClick={() => load(page.totalPages - 1)}
            disabled={pg >= page.totalPages - 1}
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40"
          >»»</button>
        </div>
      )}

      {/* 상세 모달 */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
             onClick={() => setSelected(null)}>
          <div className="w-full max-w-2xl rounded-lg bg-white p-4 shadow-xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-3">
              <h3 className="text-lg font-bold">발송 상세 #{selected.sendSeq}</h3>
              <button className="rounded p-1 hover:bg-gray-100" onClick={() => setSelected(null)}>✕</button>
            </div>
            <dl className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
              <dt className="text-gray-500">발송 시각</dt><dd className="col-span-2">{fmt(selected.dtRegist)}</dd>
              <dt className="text-gray-500">트리거 코드</dt><dd className="col-span-2 font-mono">{selected.triggerCode}</dd>
              <dt className="text-gray-500">알리고 tpl_code</dt><dd className="col-span-2 font-mono">{selected.templateCode ?? '-'}</dd>
              <dt className="text-gray-500">알리고 templtName</dt><dd className="col-span-2">{selected.templateName ?? '-'}</dd>
              <dt className="text-gray-500">수신자</dt><dd className="col-span-2">{selected.receiverPhone} · {selected.receiverName ?? '-'}</dd>
              <dt className="text-gray-500">회사 SEQ</dt><dd className="col-span-2">{selected.companySeq ?? '-'}</dd>
              <dt className="text-gray-500">상태</dt>
              <dd className="col-span-2">
                <span className={`inline-block rounded-full px-2 py-0.5 text-xs ${STATUS_COLOR[selected.resultStatus] ?? ''}`}>
                  {STATUS_LABEL[selected.resultStatus] ?? selected.resultStatus}
                </span>
              </dd>
              <dt className="text-gray-500">알리고 mid</dt><dd className="col-span-2">{selected.aligoMid ?? '-'}</dd>
              <dt className="text-gray-500">응답 코드</dt><dd className="col-span-2">{selected.responseCode ?? '-'}</dd>
              <dt className="text-gray-500">차감 크레딧</dt>
              <dd className="col-span-2">
                {selected.creditAmount ? `${selected.creditAmount}크 (TX #${selected.creditTxSeq ?? '-'})` : '-'}
              </dd>
              <dt className="text-gray-500">연관</dt>
              <dd className="col-span-2">
                {selected.sourceType ? `${selected.sourceType} #${selected.sourceSeq ?? '-'}` : '-'}
              </dd>
              <dt className="text-gray-500 col-span-3 mt-2">결과 메시지</dt>
              <dd className="col-span-3 rounded bg-gray-50 p-2 text-xs">{selected.resultMessage ?? '-'}</dd>
              <dt className="text-gray-500 col-span-3 mt-2">치환된 본문</dt>
              <dd className="col-span-3 rounded bg-gray-50 p-2 text-xs whitespace-pre-wrap font-mono">
                {selected.requestMessage ?? '(없음)'}
              </dd>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}
